<template>
    <InfiniteScroller #default="{ listPortion }" :list="trucksSorted">
        <table>
            <thead>
                <tr>
                    <th>
                        {{ $t('truck') }}
                        <IconButton @click="handleSort('name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>
                    <th>
                        {{ $t('distance') }}
                        <IconButton @click="handleSort('distance')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'distance' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>
                    <th>
                        {{ $t('fuel') }}
                        <IconButton @click="handleSort('fuel')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'fuel' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>
                    <th>
                        {{ $t('consumption') }}
                        <IconButton @click="handleSort('consumption')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'consumption' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>
                </tr>
            </thead>

            <template v-for="truck in listPortion">
                <tr :key="truck.id" @click="handleExpand(truck.id)">
                    <td>
                        {{ truck.name }}
                    </td>
                    <td>
                        {{ truck.totalDistance }}
                    </td>
                    <td>
                        {{ truck.totalFuel }}
                    </td>
                    <td>
                        {{ truck.avgConsumption }}
                    </td>
                </tr>
                <tr v-if="expandedId === truck.id" :key="truck.id + '-detail'">
                    <td colspan="4" class="detail-cell">
                        <div class="truck-details">
                            <div class="truck-details__cards">
                                <div class="truck-details__card">
                                    <p class="truck-details__card-label">
                                        {{ $t('totalDistance') }}
                                    </p>
                                    <p class="truck-details__card-value">
                                        {{ truck.totalDistance }}
                                        {{ units.odometer }}
                                    </p>
                                </div>
                                <div class="truck-details__card">
                                    <p class="truck-details__card-label">
                                        {{ $t('totalFuel') }}
                                    </p>
                                    <p class="truck-details__card-value">
                                        {{ truck.totalFuel }}
                                        {{ units.fuel_total }}
                                    </p>
                                </div>
                                <div class="truck-details__card">
                                    <p class="truck-details__card-label">
                                        {{ $t('avgConsumption') }}
                                    </p>
                                    <p class="truck-details__card-value">
                                        {{ truck.avgConsumption }}
                                        {{ units.fuel_consumption }}
                                    </p>
                                </div>
                            </div>

                            <div class="truck-details__chart-section">
                                <div class="truck-details__chart-header">
                                    <p class="truck-details__chart-title">
                                        {{ $t('dailyHistory') }}
                                    </p>
                                    <BaseMultiselect
                                        v-model="chartMetric"
                                        :options="chartMetricOptions"
                                        track-by="key"
                                        label="label"
                                        :allow-empty="false"
                                    />
                                </div>
                                <ApexChart
                                    height="180"
                                    :options="getChartOptions(truck)"
                                    :series="getChartSeries(truck)"
                                />
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
        </table>
    </InfiniteScroller>
</template>

<script>
import ApexChart from 'vue-apexcharts'

import { measurementHelper } from '@/utils'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import SortArrowIcon from '@/components/icons/SortArrowIcon'

export default {
    name: 'EcoScoreTruckTable',
    components: {
        ApexChart,
        BaseMultiselect,
        IconButton,
        InfiniteScroller,
        SortArrowIcon,
    },
    props: {
        trucks: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            chartMetric: null,
            chartMetricOptions: [
                { key: 'fuel', label: this.$t('chartFuel') },
                { key: 'distance', label: this.$t('chartDistance') },
                { key: 'consumption', label: this.$t('chartConsumption') },
            ],
            expandedId: null,
            sortBy: 'name',
            sortDirection: 1,
            units: measurementHelper.units,
        }
    },
    computed: {
        trucksSorted() {
            return this.trucks.slice().sort((a, b) => {
                let result = 0
                if (this.sortBy === 'name') {
                    result = a.name.localeCompare(b.name)
                } else if (this.sortBy === 'distance') {
                    result = a.totalDistance - b.totalDistance
                } else if (this.sortBy === 'fuel') {
                    result = a.totalFuel - b.totalFuel
                } else {
                    result = a.avgConsumption - b.avgConsumption
                }
                return result * this.sortDirection
            })
        },
    },
    mounted() {
        this.chartMetric = this.chartMetricOptions[0]
    },
    methods: {
        handleExpand(id) {
            this.expandedId = this.expandedId === id ? null : id
        },
        handleSort(column) {
            if (this.sortBy === column) {
                this.sortDirection = this.sortDirection === 1 ? -1 : 1
            } else {
                this.sortBy = column
                this.sortDirection = 1
            }
        },
        getChartOptions(truck) {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                colors: ['#2e73b8'],
                dataLabels: { enabled: false },
                xaxis: {
                    categories: truck.entries.map(entry => entry.date.slice(5)),
                },
            }
        },
        getChartSeries(truck) {
            const metric = this.chartMetric || this.chartMetricOptions[0]
            const data = truck.entries.map(entry => {
                switch (metric.key) {
                    case 'consumption':
                        return entry.total_distance > 0
                            ? Math.round(
                                  (entry.total_fuel / entry.total_distance) *
                                      1000
                              ) / 10
                            : 0
                    case 'distance':
                        return entry.total_distance
                    case 'fuel':
                        return entry.total_fuel
                }
            })
            return [{ name: metric.label, data }]
        },
    },
}
</script>

<i18n>
{
    "en": {
        "avgConsumption": "Avg consumption",
        "chartConsumption": "L/100km",
        "chartDistance": "Distance (km)",
        "chartFuel": "Fuel (L)",
        "consumption": "L/100km",
        "dailyHistory": "Daily history",
        "distance": "Distance (km)",
        "fuel": "Fuel (L)",
        "totalDistance": "Total distance",
        "totalFuel": "Total fuel",
        "truck": "Truck"
    },
    "de": {
        "avgConsumption": "Ø Verbrauch",
        "chartConsumption": "L/100km",
        "chartDistance": "Distanz (km)",
        "chartFuel": "Kraftstoff (L)",
        "consumption": "L/100km",
        "dailyHistory": "Tagesverlauf",
        "distance": "Distanz (km)",
        "fuel": "Kraftstoff (L)",
        "totalDistance": "Gesamtdistanz",
        "totalFuel": "Gesamtverbrauch",
        "truck": "Fahrzeug"
    },
    "fr": {
        "avgConsumption": "Consommation moy.",
        "chartConsumption": "L/100km",
        "chartDistance": "Distance (km)",
        "chartFuel": "Carburant (L)",
        "consumption": "L/100km",
        "dailyHistory": "Historique journalier",
        "distance": "Distance (km)",
        "fuel": "Carburant (L)",
        "totalDistance": "Distance totale",
        "totalFuel": "Carburant total",
        "truck": "Camion"
    },
    "it": {
        "avgConsumption": "Consumo medio",
        "chartConsumption": "L/100km",
        "chartDistance": "Distanza (km)",
        "chartFuel": "Carburante (L)",
        "consumption": "L/100km",
        "dailyHistory": "Andamento giornaliero",
        "distance": "Distanza (km)",
        "fuel": "Carburante (L)",
        "totalDistance": "Distanza totale",
        "totalFuel": "Carburante totale",
        "truck": "Camion"
    }
}
</i18n>

<style lang="scss" scoped>
table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        height: 100%;

        &:not(:first-child) {
            cursor: pointer;

            &:hover {
                background-color: $color-gray-lighter;
            }

            & + tr {
                td {
                    border-top: $style-border;
                }
            }
        }
    }

    th {
        padding: 1rem;
        border-bottom: $style-border;
        text-align: left;
        white-space: nowrap;
    }

    td {
        padding: 0.5rem 1rem;
        white-space: nowrap;
    }

    th,
    td {
        &:first-child {
            padding-left: 2rem;
        }

        &:last-child {
            padding-right: 2rem;
        }

        &:not(:last-child) {
            width: 1%;
        }
    }
}

.detail-cell {
    padding: 1rem 2rem;
    background-color: $color-gray-lighter;
    cursor: default;
}

.truck-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75rem;
    }

    &__card {
        padding: 1rem;
        border: $style-border;
        border-radius: 0.75rem;
        text-align: center;
        background: #fff;
    }

    &__card-label {
        font-size: 0.75rem;
        color: $color-gray-darker;
    }

    &__card-value {
        font-size: 1.25rem;
        font-weight: 600;
        color: $color-text;
    }

    &__chart-section {
        padding: 1rem;
        border: $style-border;
        border-radius: 0.75rem;
        background: #fff;
    }

    &__chart-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
    }

    &__chart-title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: $color-text;
    }

    @include respond-to('for-mobile-down') {
        &__cards {
            grid-template-columns: 1fr;
        }
    }
}
</style>
